import { useInfiniteQuery } from '@tanstack/react-query';
import { get } from '../api/api';
import { API_URLS } from '../constants/apiUrl';
import { QueryParamObject, toQueryString } from '../constants/utilityFunctions';
import { ICustomerType } from '../constants/customer';
interface IUseGetCustomerMattersById {
  relations?: string[];
  sortBy?: string[];
  extraFilters?: Record<string, string | number | undefined>;
  limit?: number;
  search?: string;
  fields?: string[];
  type?: string;
  customerId?: string;
  withFavoriteInformation?: boolean;
  withHasSubMatters?: boolean;
}
export const useGetCustomerMattersById = (params: IUseGetCustomerMattersById) => {
  const fetchCustomersMatter = async ({ pageParam = 1 }) => {
    const {
      relations: customersMatterRelations,
      extraFilters: filters,
      type,
      customerId,
      ...rest
    } = params;
    const queryString = new URLSearchParams(
      Object.keys(rest)?.length ? toQueryString(rest as QueryParamObject) : '',
    );

    Object.entries(filters ?? {}).forEach(([key, value]) => {
      if (value) queryString.append(`filter.${key}`, String(value));
    });
    (customersMatterRelations ?? []).forEach((rel) => queryString.append('relations', rel));

    queryString.append('page', String(pageParam));
    const url =
      type === ICustomerType.COMPANY
        ? `${API_URLS.COMPANY_MATTER_LIST.replace(':id', customerId ?? '')}?${queryString.toString()}`
        : `${API_URLS.PRIVATE_PERSON_MATTER_LIST.replace(':id', customerId ?? '')}?${queryString.toString()}`;
    try {
      return await get(url);
    } catch (error) {
      console.error('Error fetching customer matter list:', error);
      throw new Error(
        (error as { message: string }).message || 'Failed to fetch customer matter list',
      );
    }
  };

  const {
    data,
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  } = useInfiniteQuery({
    initialPageParam: 1,
    queryKey: ['customer-matter-list', params],
    queryFn: fetchCustomersMatter,
    enabled: !!params?.customerId,
    getNextPageParam: (lastPage, allPages) => {
      const totalPageCount = lastPage?.meta?.totalItems ?? 0;
      const fetchedPageCount = allPages.flatMap((page) => page.data).length;
      return fetchedPageCount < totalPageCount ? allPages.length + 1 : undefined;
    },
  });
  return {
    data: data?.pages.flatMap((page) => page.data) ?? [],
    isLoading,
    error,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    refetch,
    isFetching,
  };
};
